import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FormProvider, useForm } from "react-hook-form";
import { Navigate, useSearchParams } from "react-router-dom";
import { useContext, useState } from "react";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import UserSettingsPage from "pages/UserSettings/UserSettingsPage";
import { ROUTES, TOAST_CONFIG, UPLOAD_TO_S3, USER_SELF } from "../../constants";
import { patch, put } from "../../utils/request";
import Loading from "../../components/atoms/Loading";
import UserPersonalInfoForm from "../../components/organisms/UserPersonalInfoForm";
import {
  prependHTTPsForSocialLinks,
  appendJsonToFormData,
  validationForURLPattern,
  handleSetErrorAndSwitchFormState,
} from "../../utils/HelperFunctions";
import { UserContext } from "../../components/context/UserContext";

type UserOnBoardingForm = {
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  twitter_link: string | null;
  instagram_link: string | null;
  other_link: string;
  profile_image: any;
  profile_image_url: string | null;
};

type FormFieldsType =
  | "first_name"
  | "last_name"
  | "username"
  | "email"
  | "twitter_link"
  | "instagram_link"
  | "other_link"
  | "profile_image_url";

const formFields = {
  first_name: "0",
  last_name: "0",
  username: "0",
  email: "0",
  twitter_link: "0",
  instagram_link: "0",
  other_link: "0",
  profile_image_url: "0",
};

const schema = yup.object().shape({
  first_name: yup.string().trim(),
  last_name: yup.string().trim(),
  username: yup.string().trim().required("* Required field"),
  email: yup.string().trim().required("* Required field").email(),
  twitter_link: validationForURLPattern("twitter_link"),
  instagram_link: validationForURLPattern("instagram_link"),
  other_link: validationForURLPattern("other_link"),
});

function CreateAccount({ defaultValues }: { defaultValues: User }) {
  const formMethods = useForm<UserOnBoardingForm>({
    resolver: yupResolver(schema),
    // @ts-ignore
    defaultValues,
  });

  const { handleSubmit, setError } = formMethods;
  const { setupUserAccountAndAirDropMagnet } = useContext(UserContext);
  const [updateLoading, setUpdateLoading] = useState(false);
  const currentStep: number = 0;

  const handleProfileImageUploadToS3 = async (file: any) => {
    if (!file) return null;
    const uploadedImage = await put<any>(UPLOAD_TO_S3, appendJsonToFormData(file));
    if (uploadedImage?.file_url) {
      return uploadedImage.file_url;
    }
    return toast.error("Failed to upload image", TOAST_CONFIG);
  };

  //   function for posting data to the server
  const handleCreateUserAccount = async (userData: UserOnBoardingForm) => {
    setUpdateLoading(true);
    const profileImageUrl = userData.profile_image
      ? await handleProfileImageUploadToS3(userData.profile_image)
      : undefined;

    const handleCreateAccountSuccess = async () => {
      await setupUserAccountAndAirDropMagnet();
      setUpdateLoading(false);
    };

    const handleCreateAccountError = (errorMessage: string, error: any, errorData: any) => {
      toast.error("Failed to Create Account", TOAST_CONFIG);
      handleSetErrorAndSwitchFormState<FormFieldsType>(errorData, formFields, setError);
      setUpdateLoading(false);
    };

    const payload = {
      first_name: userData.first_name,
      last_name: userData.last_name,
      profile_image_url: profileImageUrl,
      username: userData.username,
      email: userData.email,
      twitter_link: userData.twitter_link,
      instagram_link: userData.instagram_link,
      other_link: userData.other_link,
    };

    // @ts-ignore
    const formData = prependHTTPsForSocialLinks(payload);
    patch(USER_SELF, formData, {
      processData: handleCreateAccountSuccess,
      processError: handleCreateAccountError,
    });
  };

  const onSubmit = (data: UserOnBoardingForm) => {
    handleCreateUserAccount(data);
  };

  const form = <UserPersonalInfoForm isOnboard={!!defaultValues.email} />;

  return (
    <>
      <Helmet>
        <title>Create New Account - MAGNETIQ</title>
      </Helmet>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...formMethods}>
        {updateLoading && <Loading />}
        <UserSettingsPage
          isOnboard={false}
          currentStep={currentStep}
          handleSubmit={handleSubmit(onSubmit)}
          form={form}
        />
      </FormProvider>
    </>
  );
}

export default function CreateAccountPage() {
  const { userDetails, isUserLoggedIn, loading, token } = useContext(UserContext);

  const [searchParams] = useSearchParams();
  const redirectURI = searchParams.get("redirectURI");

  if (loading) return <Loading />;

  if (token && !userDetails) return null;

  if (isUserLoggedIn) {
    // Either magnetic program is 1(Account Setup Complete) or 2(Onboarding Complete)
    return userDetails?.magnetiq_program_status ? (
      // Check if there is a redirect URI
      <Navigate to={redirectURI || ROUTES.USER_PROFILE_PAGE} />
    ) : (
      // @ts-ignore
      <CreateAccount defaultValues={userDetails} />
    );
  }

  return <Navigate to={ROUTES.SIGNIN} />;
}
